import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import GlassOverlay from './GlassOverlay'
import { useTranslation } from '../hooks/useTranslation'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { priceHistoryService } from '../services/api'
import { formatEuroFr } from '../utils/editProductOutcome'
import {
  HISTORY_PERIODS,
  buildHistoryView,
  formatAxisDate,
  formatAxisEuro,
  formatLongDate,
  historyErrorMessage,
  periodIdFromIndex,
  periodIndexFromId,
} from '../utils/priceHistoryChart'
import './AddProductModal.css'
import './PriceHistoryModal.css'

function CloseGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  )
}

function PeriodSlider({ value, onCommit, reducedMotion = false }) {
  const listId = useId()
  const liveId = useId()
  const draggingRef = useRef(false)
  const draftRef = useRef(periodIndexFromId(value))
  const onCommitRef = useRef(onCommit)
  const [draft, setDraft] = useState(() => periodIndexFromId(value))
  const [dragging, setDragging] = useState(false)
  const last = HISTORY_PERIODS.length - 1

  onCommitRef.current = onCommit
  draftRef.current = draft

  useEffect(() => {
    if (draggingRef.current) return
    setDraft(periodIndexFromId(value))
  }, [value])

  const commitIndex = (index) => {
    const next = periodIdFromIndex(index)
    setDraft(periodIndexFromId(next))
    onCommitRef.current(next)
  }

  const finishDrag = () => {
    if (!draggingRef.current) return
    draggingRef.current = false
    setDragging(false)
    commitIndex(draftRef.current)
  }

  useEffect(() => {
    if (!dragging) return undefined
    window.addEventListener('pointerup', finishDrag)
    window.addEventListener('pointercancel', finishDrag)
    return () => {
      window.removeEventListener('pointerup', finishDrag)
      window.removeEventListener('pointercancel', finishDrag)
    }
  }, [dragging])

  const readIndex = (event) => periodIndexFromId(periodIdFromIndex(event.currentTarget.value))

  return (
    <div
      className={`price-history-slider${dragging ? ' is-dragging' : ''}${reducedMotion ? ' is-static' : ''}`}
      style={{ '--period-progress': String(last === 0 ? 0 : draft / last) }}
    >
      <div className="price-history-slider-control">
        <div className="price-history-slider-track" aria-hidden="true">
          <span className="price-history-slider-fill" />
          <span className="price-history-slider-thumb" />
          {HISTORY_PERIODS.map((period, index) => (
            <span
              key={period.id}
              className={`price-history-slider-mark${draft === index ? ' is-active' : ''}`}
              style={{ left: `${(index / last) * 100}%` }}
            />
          ))}
        </div>
        <input
          type="range"
          className="price-history-slider-input"
          min={0}
          max={last}
          step={1}
          value={draft}
          list={listId}
          aria-label="Période affichée"
          aria-describedby={liveId}
          aria-valuemin={0}
          aria-valuemax={last}
          aria-valuenow={draft}
          aria-valuetext={HISTORY_PERIODS[draft].label}
          onPointerDown={() => {
            draggingRef.current = true
            setDragging(true)
          }}
          onPointerUp={finishDrag}
          onPointerCancel={finishDrag}
          onInput={(event) => {
            setDraft(readIndex(event))
          }}
          onChange={(event) => {
            const index = readIndex(event)
            setDraft(index)
            if (!draggingRef.current) commitIndex(index)
          }}
        />
        <datalist id={listId}>
          {HISTORY_PERIODS.map((period, index) => (
            <option key={period.id} value={index} label={period.label} />
          ))}
        </datalist>
      </div>
      <div className="price-history-slider-labels">
        {HISTORY_PERIODS.map((period, index) => (
          <button
            key={period.id}
            type="button"
            className={`price-history-slider-label${draft === index ? ' is-active' : ''}`}
            tabIndex={-1}
            onClick={() => commitIndex(index)}
          >
            <span className="is-full">{period.label}</span>
            <span className="is-short">{period.short}</span>
          </button>
        ))}
      </div>
      <p id={liveId} className="price-history-slider-live">
        Période sélectionnée : {HISTORY_PERIODS[draft].label}
      </p>
    </div>
  )
}

function HistoryTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  if (!row || row.price == null) return null
  return (
    <div className="price-history-tooltip">
      <div className="price-history-tooltip-date">{formatLongDate(row.checkedAt)}</div>
      <div className="price-history-tooltip-price">{formatEuroFr(row.price)}</div>
    </div>
  )
}

export default function PriceHistoryModal({
  isOpen,
  onClose,
  itemId,
  itemName = '',
  itemImage = '',
  returnFocusRef,
  preview = null,
}) {
  const titleId = useId()
  const hintId = useId()
  const closeRef = useRef(null)
  const { t } = useTranslation()
  const reducedMotion = usePrefersReducedMotion()
  const [periodId, setPeriodId] = useState('all')
  const [brokenImage, setBrokenImage] = useState(false)

  useEffect(() => {
    setPeriodId('all')
    setBrokenImage(false)
  }, [itemId])

  const query = useQuery({
    queryKey: ['priceHistory', itemId],
    queryFn: ({ signal }) => priceHistoryService.getHistory(itemId, { limit: 500, signal }),
    enabled: isOpen && Boolean(itemId) && !preview,
    retry: (count, error) => {
      const status = error?.response?.status
      if (status === 401 || status === 403 || status === 404) return false
      return count < 1
    },
    refetchOnWindowFocus: false,
  })

  const payload = preview ? preview.data : query.data
  const error = preview ? preview.error : query.error
  const fetchingWrongItem = Boolean(
    payload
    && itemId
    && payload.item_id != null
    && String(payload.item_id) !== String(itemId),
  )
  const loading = preview
    ? Boolean(preview.loading)
    : Boolean(itemId) && ((query.isFetching && !payload && !error) || fetchingWrongItem)

  const view = useMemo(
    () => buildHistoryView({
      loading: loading || fetchingWrongItem,
      error,
      payload: fetchingWrongItem ? null : payload,
      itemId,
      periodId,
    }),
    [loading, fetchingWrongItem, error, payload, itemId, periodId],
  )

  const showChart = view.state === 'single' || view.state === 'multiple'
  const stroke = 'rgba(125, 211, 252, 0.95)'

  return (
    <GlassOverlay
      isOpen={isOpen}
      onClose={onClose}
      labelledBy={titleId}
      describedBy={hintId}
      panelClassName="price-history-dialog"
      rootClassName="price-history-root"
      initialFocusRef={closeRef}
      returnFocusRef={returnFocusRef}
    >
      <div className="price-history">
        <header className="price-history-header">
          <div className="price-history-heading">
            <h2 id={titleId}>{t('priceHistory') || 'Historique des prix'}</h2>
            <p id={hintId} className="price-history-kicker">
              Relevés déjà enregistrés, sans actualisation automatique.
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="add-product-icon-btn is-spin is-plain"
            onClick={onClose}
            aria-label="Fermer"
          >
            <CloseGlyph />
          </button>
        </header>

        <div className="price-history-body">
          <div className="price-history-product">
            <div className={`price-history-thumb${itemImage && !brokenImage ? ' is-ready' : ''}`}>
              {itemImage && !brokenImage ? (
                <img src={itemImage} alt="" onError={() => setBrokenImage(true)} />
              ) : (
                <span aria-hidden="true" />
              )}
            </div>
            <p className="price-history-name" title={itemName || undefined}>
              {itemName || 'Produit'}
            </p>
          </div>

          {view.state !== 'loading' && view.state !== 'error' ? (
            <PeriodSlider
              value={periodId}
              onCommit={setPeriodId}
              reducedMotion={reducedMotion}
            />
          ) : null}

          {view.state === 'loading' ? (
            <div className="price-history-status" role="status">
              <div className="price-history-skel" />
              <p>{t('loading') || 'Chargement…'}</p>
            </div>
          ) : null}

          {view.state === 'error' ? (
            <div className="price-history-status is-error" role="alert">
              <p>{historyErrorMessage(error)}</p>
              <button
                type="button"
                className="price-history-retry"
                onClick={() => {
                  if (preview?.onRetry) preview.onRetry()
                  else query.refetch()
                }}
              >
                Réessayer
              </button>
            </div>
          ) : null}

          {view.state === 'empty' ? (
            <div className="price-history-status">
              <p>{t('noPriceHistory') || 'Aucun relevé de prix enregistré'}</p>
              <p className="price-history-hint">
                {t('priceHistoryHint') || 'L’historique apparaîtra après de prochains relevés enregistrés.'}
              </p>
            </div>
          ) : null}

          {view.state === 'period-empty' ? (
            <div className="price-history-status">
              <p>Aucun relevé sur cette période.</p>
              <p className="price-history-hint">
                {view.allPoints.length} observation{view.allPoints.length > 1 ? 's' : ''} au total.
              </p>
            </div>
          ) : null}

          {view.state === 'mixed' ? (
            <div className="price-history-status is-error">
              <p>Cet historique mélange plusieurs devises. L’affichage est interrompu pour éviter un graphique trompeur.</p>
            </div>
          ) : null}

          {showChart && view.summary ? (
            <>
              <div className="price-history-stats">
                <div>
                  <span>Dernier prix</span>
                  <strong>{formatEuroFr(view.summary.last)}</strong>
                </div>
                <div>
                  <span>Minimum</span>
                  <strong>{formatEuroFr(view.summary.min)}</strong>
                </div>
                <div>
                  <span>Maximum</span>
                  <strong>{formatEuroFr(view.summary.max)}</strong>
                </div>
              </div>

              <div className="price-history-chart-frame" data-chart-state={view.state}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={view.rows} margin={{ top: 18, right: 18, left: 10, bottom: 8 }}>
                    <CartesianGrid stroke="currentColor" strokeOpacity={0.16} strokeDasharray="4 6" vertical={false} />
                    <XAxis
                      dataKey="time"
                      type="number"
                      domain={view.bounds.x}
                      ticks={view.ticks}
                      tickFormatter={(value) => formatAxisDate(value, view.spanMs)}
                      stroke="currentColor"
                      tick={{ fill: 'currentColor', fontSize: 11 }}
                      minTickGap={22}
                    />
                    <YAxis
                      domain={view.bounds.y}
                      tickFormatter={formatAxisEuro}
                      stroke="currentColor"
                      tick={{ fill: 'currentColor', fontSize: 11 }}
                      width={72}
                    />
                    <Tooltip
                      content={<HistoryTooltip />}
                      cursor={{ stroke: 'currentColor', strokeOpacity: 0.28 }}
                    />
                    <Line
                      type="linear"
                      dataKey="price"
                      stroke={stroke}
                      strokeWidth={view.state === 'multiple' ? 2 : 0}
                      dot={{ r: view.state === 'single' ? 6 : 4, fill: stroke, strokeWidth: 0 }}
                      activeDot={{ r: 7, fill: '#fff', stroke, strokeWidth: 2 }}
                      isAnimationActive={!reducedMotion}
                      connectNulls={false}
                      name="Prix"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <p className="price-history-caption">
                {view.state === 'single'
                  ? 'Un seul relevé est enregistré. Aucune courbe n’est inventée.'
                  : 'Les segments relient des relevés ponctuels ; aucun prix intermédiaire n’est inventé.'}
              </p>
              <p className="price-history-observed">
                Dernière observation : {formatLongDate(view.summary.lastAt)}
              </p>
            </>
          ) : null}
        </div>
      </div>
    </GlassOverlay>
  )
}
